/**
 * Centralized profile slug constants and helpers.
 *
 * These must stay in sync with backend/pkg/artifact/types.go.
 * When adding a new artifact type, update both backend and this file.
 */

export const PROFILE_SLUGS = {
  COACHING_CONVERSATION_SUMMARY: 'coaching-conversation-summary',
  COACHING_CONVERSATIONS_SUMMARY: 'coaching-conversations-summary',
  COACHING_FEEDBACK: 'coaching-feedback',
  COACHING_FEEDBACK_ARC: 'coaching-feedback-arc',
  CHEM_CALL_REFLECTION: 'chem-call-reflection',
  MEMBER_REFLECTION: 'member-reflection',
  CHEM_CALL_REFLECTION_MEMBER: 'chem-call-reflection-member',
  MEMBER_REFLECTION_ARC: 'member-reflection-arc',
  SESSION_PREP_COACH: 'session-prep-coach',
  SESSION_PREP_MEMBER: 'session-prep-member',
  MILESTONES_AND_WINS: 'milestones-and-wins',
  DRIVE1ON1_SUMMARY: 'drive1on1-summary',
} as const;

export type ProfileSlug = (typeof PROFILE_SLUGS)[keyof typeof PROFILE_SLUGS];

/** Profile slugs used for related-artifact display. Excludes milestones-and-wins. */
export const PROFILE_SLUGS_FOR_ARTIFACT_DISPLAY: readonly string[] = [
  PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY,
  PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY,
  PROFILE_SLUGS.CHEM_CALL_REFLECTION,
  PROFILE_SLUGS.MEMBER_REFLECTION,
  PROFILE_SLUGS.CHEM_CALL_REFLECTION_MEMBER,
  PROFILE_SLUGS.MEMBER_REFLECTION_ARC,
  PROFILE_SLUGS.COACHING_FEEDBACK,
  PROFILE_SLUGS.COACHING_FEEDBACK_ARC,
  PROFILE_SLUGS.SESSION_PREP_COACH,
  PROFILE_SLUGS.SESSION_PREP_MEMBER,
];

/** Profile slugs that assign multi-transcript artifacts to the most recent transcript only. */
export function isProfileArcForAssignment(profileSlug: string): boolean {
  return (
    profileSlug === PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY ||
    profileSlug === PROFILE_SLUGS.COACHING_FEEDBACK_ARC ||
    profileSlug === PROFILE_SLUGS.MEMBER_REFLECTION_ARC
  );
}

/** Returns the default profile slug for summary artifacts based on document count. */
export function getProfileForSummaryDocumentCount(count: number): string {
  return count >= 2
    ? PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY
    : PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY;
}

/** Maps profile slug to analytics artifact_type. */
export function profileSlugToArtifactType(profileSlug: string): string | null {
  switch (profileSlug) {
    case PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY:
      return 'coaching_session_summary';
    case PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY:
      return 'coaching_arc_summary';
    case PROFILE_SLUGS.COACHING_FEEDBACK:
      return 'coaching_feedback';
    case PROFILE_SLUGS.COACHING_FEEDBACK_ARC:
      return 'coaching_feedback_arc';
    case PROFILE_SLUGS.CHEM_CALL_REFLECTION:
      return 'chem_call_reflection';
    case PROFILE_SLUGS.MEMBER_REFLECTION:
      return 'member_reflection';
    case PROFILE_SLUGS.CHEM_CALL_REFLECTION_MEMBER:
      return 'chem_call_reflection_member';
    case PROFILE_SLUGS.MEMBER_REFLECTION_ARC:
      return 'member_reflection_arc';
    case PROFILE_SLUGS.SESSION_PREP_COACH:
      return 'session_prep_coach';
    case PROFILE_SLUGS.SESSION_PREP_MEMBER:
      return 'session_prep_member';
    case PROFILE_SLUGS.MILESTONES_AND_WINS:
      return 'milestones_and_wins';
    case PROFILE_SLUGS.DRIVE1ON1_SUMMARY:
      return 'drive1on1_summary';
    default:
      return null;
  }
}

/** Controls whether artifacts of a profile can be shared with members. */
export function isProfileShareable(profileSlug: string): boolean {
  const normalized = profileSlug.trim();
  return (
    normalized === PROFILE_SLUGS.COACHING_CONVERSATION_SUMMARY ||
    normalized === PROFILE_SLUGS.COACHING_CONVERSATIONS_SUMMARY
  );
}
